'use client'

import { type ReactNode, useMemo } from 'react'
import { type EmcnLabels, EmcnLabelsProvider } from '@sim/emcn'
import { useLocale, useTranslations } from 'next-intl'
import type { Locale } from '@/i18n/config'

/** Plain texts of `common.emcn`; their keys equal the `EmcnLabels` keys. */
const TEXT_KEYS = [
  'cancel',
  'close',
  'dialog',
  'confirm',
  'all',
  'back',
  'next',
  'done',
  'wizard',
  'dismiss',
  'retry',
  'tryAgain',
  'select',
  'search',
  'copy',
  'copied',
  'copyToClipboard',
  'showPassword',
  'hidePassword',
  'dropFilesOrBrowse',
  'dropFiles',
  'invalidSelection',
  'enterEmails',
  'addAnother',
  'invalidEmail',
  'invalidEmailOrDomain',
  'searchAllOptions',
  'loadMore',
  'searchingOptions',
  'loadingOptions',
  'noMatchesInPartialResults',
  'noMatchesInLoadedOptions',
  'noOptionsLoaded',
  'noMatchingOptions',
  'noOptionsAvailable',
  'noResults',
  'showingPartialResults',
  'openOptions',
  'closeOptions',
  'time',
  'selectTime',
  'selectDate',
  'selectDateRange',
  'today',
  'clear',
  'apply',
  'to',
  'previousMonth',
  'nextMonth',
  'chartValue',
  'chartValuesByDate',
  'chartTotal',
  'chartDate',
  'noData',
  'loadingValue',
  'expand',
  'collapse',
  'moreInformation',
  'mediaViewer',
  'closeMediaViewer',
  'mediaZoom',
  'zoomOut',
  'zoomIn',
  'mediaControls',
  'seekVideo',
  'play',
  'pause',
  'mute',
  'unmute',
  'newTab',
  'tabs',
  'enterValues',
  'tagInput',
  'addTag',
  'uploadFile',
  'dropFile',
  'dismissNotification',
  'notifications',
  'required',
] as const satisfies readonly (keyof EmcnLabels)[]

/**
 * Date formatting, first weekday and clock per UI language; English keeps
 * emcn's US defaults. `hourCycle` drives emcn and the table date cells; places
 * formatting through `Intl` with the locale (`formatLogTimestamp`, `useFormatter`)
 * follow the language's own clock, so switching English to 24 h means those too.
 */
const CALENDAR_CONVENTIONS: Record<
  Locale,
  Pick<EmcnLabels, 'locale' | 'weekStartsOn' | 'hourCycle'>
> = {
  en: { locale: 'en-US', weekStartsOn: 0, hourCycle: 12 },
  de: { locale: 'de-DE', weekStartsOn: 1, hourCycle: 24 },
}

/** 2024-01-07 is a Sunday; `weekdays` is Sunday-first, emcn rotates it by `weekStartsOn`. */
const FIRST_SUNDAY_UTC = Date.UTC(2024, 0, 7)
const DAY_MS = 24 * 60 * 60 * 1000

/** Month and weekday names in the UI language; English matches emcn's own. */
function calendarNames(locale: string): Pick<EmcnLabels, 'months' | 'weekdays'> {
  const month = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' })
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
  return {
    months: Array.from({ length: 12 }, (_, i) => month.format(Date.UTC(2024, i, 1))),
    weekdays: Array.from({ length: 7 }, (_, i) =>
      weekday
        .format(FIRST_SUNDAY_UTC + i * DAY_MS)
        .replace('.', '')
        .slice(0, 2)
    ),
  }
}

/**
 * omnyqra: translated default texts of the emcn components (Omnyqra/sim#67),
 * see FORK.md.
 *
 * Mounted once inside the root `NextIntlClientProvider`. Nested intl providers
 * (`AllMessagesProvider`, `f/[token]`) only swap the messages, the emcn
 * context from here stays in place below them.
 */
export function TranslatedEmcnLabels({ children }: { children: ReactNode }) {
  const t = useTranslations('common.emcn')
  const locale = useLocale()
  const labels = useMemo<EmcnLabels>(
    () => ({
      ...(Object.fromEntries(TEXT_KEYS.map((key) => [key, t(key)])) as Record<
        (typeof TEXT_KEYS)[number],
        string
      >),
      ...CALENDAR_CONVENTIONS[locale],
      ...calendarNames(CALENDAR_CONVENTIONS[locale].locale),
      loadingChart: (title) => (title ? t('loadingChart', { title }) : t('loadingChartUntitled')),
      selectedCount: (count) => t('selectedCount', { count }),
      highlightSeries: (label, value) =>
        value ? t('highlightSeriesValue', { label, value }) : t('highlightSeries', { label }),
      backgroundActivity: (title) => t('backgroundActivity', { title }),
      maxTabs: (max) => t('maxTabs', { max }),
      closeTab: (title) => t('closeTab', { title }),
      removeTag: (value) => t('removeTag', { value }),
      resetZoom: (percent) => t('resetZoom', { percent }),
      playbackPosition: (current, total) => t('playbackPosition', { current, total }),
    }),
    [t, locale]
  )
  return <EmcnLabelsProvider labels={labels}>{children}</EmcnLabelsProvider>
}
